/**
 * Services page — owner directive (Feb 2026): keep this page DEAD SIMPLE.
 *
 * Goal:
 *   - Explain HOW NoJo is able to deliver everything it delivers (AI dev,
 *     AI processing, AI theory, AI consulting).
 *   - Surface the 3 operated services as a clean 3-card menu, each with a
 *     big "View detail page" button.
 *   - Drive every visitor into a detail page where the full info lives.
 *   - Mention AI Consulting as a case-by-case offering (rare; we are busy
 *     with our own services; available on inquiry only).
 *
 * Everything else (stats, plans, fees, partner snapshots, full features,
 * featured completions) lives on the detail pages — single source of truth.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Brain,
  Cog,
  Cpu,
  Lightbulb,
  ShieldCheck,
} from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import PageSideAura from "@/components/motion/PageSideAura";
import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import { SERVICE_CONTENT } from "@/data/serviceContent";
import { IMG } from "@/data/imageLibrary";
import { AI_TEAM, TEAM_WORKSPACE_PHOTOS } from "@/data/aiTeam";

const ORDER = ["japanetccard", "japantollreceipts", "japanchangemoney"];

const WORDMARK_MAP = {
  japanetccard: IMG.japanetccardWordmark,
  japantollreceipts: IMG.japantollreceiptsWordmark,
  japanchangemoney: IMG.japanchangemoneyWordmark,
};

const SOFT_BG_MAP = {
  japanetccard: "#F4EEF9",
  japantollreceipts: "#ECF6EC",
  japanchangemoney: "#E8F0FA",
};

export default function ServicesPage({ lang }) {
  const isJa = lang === "ja";

  const capabilities = [
    {
      icon: Cpu,
      titleEn: "AI Development",
      titleJa: "AI 開発",
      bodyEn:
        "We train role-specific LLM workflows for every operational pattern we run — registration packs, toll-record extraction, exchange-compliance reads.",
      bodyJa:
        "登録パック、通行記録の抽出、両替コンプライアンスの読み取りなど、当社が運用するすべての業務パターンに対し、役割別にLLMワークフローをトレーニングしています。",
    },
    {
      icon: Cog,
      titleEn: "AI Processing",
      titleJa: "AI 処理",
      bodyEn:
        "Every artifact passes through three Senior AI Analysts, a QA Lead, and a Director — the same five-member pipeline behind every service we offer.",
      bodyJa:
        "すべての成果物は、3名のシニアAIアナリスト・QAリード・ディレクターを経由します。当社のすべてのサービスを支える、5名体制の同じパイプラインです。",
    },
    {
      icon: Brain,
      titleEn: "AI Theory",
      titleJa: "AI 理論",
      bodyEn:
        "We invest in how each model is actually trained, evaluated and bounded — so judgments stay consistent across language, format and edge case.",
      bodyJa:
        "各モデルがどのように学習・評価・制御されるかを丁寧に設計しています。言語・形式・例外ケースを横断して、判断の一貫性を保つためです。",
    },
    {
      icon: Lightbulb,
      titleEn: "AI Consulting",
      titleJa: "AI コンサルティング",
      bodyEn:
        "On a strictly case-by-case basis, we share our operating playbook with select organizations that need expertise designing their own AI workflows.",
      bodyJa:
        "厳格に案件単位で、自社のAIワークフロー設計に専門知識を必要とする少数の組織に、当社の運用ノウハウを共有しています。",
    },
  ];

  return (
    <article data-testid="services-page" className="bg-white">
      <SEO {...PAGE_SEO.services[lang]} path={`/${lang}/services`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "運営サービス" : "Services", url: `/${lang}/services` },
        ])}
      />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-[#5B2E83] text-white" data-testid="services-hero">
        <BrandGraphic
          variant="wave"
          color="#7AB8E8"
          className="absolute inset-0 w-full h-full opacity-[0.10] pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <Reveal as="div" variant="up" className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 ring-1 ring-white/15 mb-5">
              <Cpu className="h-3.5 w-3.5 text-[#7AB8E8]" strokeWidth={2.25} />
              <span className="text-[10.5px] tracking-[0.26em] font-mono-display uppercase font-bold text-[#7AB8E8]">
                {isJa ? "私たちのサービス" : "Our Services"}
              </span>
            </div>
            <h1
              className={[
                isJa ? "font-ja" : "font-display",
                "text-white text-4xl md:text-5xl lg:text-[60px] font-bold tracking-tighter leading-[1.04]",
              ].join(" ")}
            >
              {isJa
                ? "私たちのカスタマーサービスは、日本でNo.1を目指して磨き続けています。"
                : "Our customer service is second to none in Japan."}
            </h1>
            <p className="mt-5 text-[16px] md:text-[17px] leading-[1.85] text-white/75 max-w-2xl">
              {isJa
                ? "私たちの軸は、お客様一人ひとりを丁寧に支えることです。少人数のチームでも、日本国内のどこにも負けないサポート品質を届け続けるために、業務の裏側を支える独自の技術とAIワークフローを内製してきました。テクノロジーはあくまでも手段 ─ ETCカード、通行記録、準備中の両替まで、すべてのサービスで最初に大切にしているのは「人がきちんと向き合うこと」です。"
                : "What sits at the center of NoJo Enterprise is one promise: customers come first, always. We're a small team that works around the clock to deliver a level of customer support that is, frankly, unmatched in Japan — and the only reason we can do that is the proprietary technology and the trained AI workflows we built ourselves to support every interaction. Technology is the support layer. The service is the product. ETC cards, toll records and the planned exchange service all share the same commitment underneath."}
            </p>
          </Reveal>
          <Reveal as="div" variant="up" delay={120} className="lg:col-span-5">
            <div
              className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 bg-[#3F1F5B]"
              style={{ aspectRatio: "16/10" }}
            >
              <img
                src={TEAM_WORKSPACE_PHOTOS[2].src}
                alt={isJa ? "JapanETCcard・JapanTollReceipts・JapanChangeMoneyを支えるNoJo EnterpriseのAIオペレーションチーム" : "NoJo Enterprise AI Operations team supporting JapanETCcard, JapanTollReceipts and JapanChangeMoney"}
                className="block w-full h-full object-contain"
                loading="eager"
              />
              <div aria-hidden className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-900/70 via-slate-900/15 to-transparent pointer-events-none" />
              <div className="absolute bottom-3 left-4 right-4">
                <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#7AB8E8]">
                  {isJa ? TEAM_WORKSPACE_PHOTOS[2].captionJa : TEAM_WORKSPACE_PHOTOS[2].captionEn}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== SERVICE CARDS — the 3-button menu ===== */}
      <section className="bg-[#F8F9FA] border-b border-[#E2E8F0] relative overflow-hidden" data-testid="services-menu">
        {/* Soft layered colour blooms behind the intro — the three brand
            accents (purple JEC, green JTR, blue JCM) bleed in from the
            left, signalling the section's "three services" theme without
            adding visual clutter. */}
        <span
          aria-hidden
          className="pointer-events-none absolute -top-10 -left-20 w-[420px] h-[420px] rounded-full opacity-[0.10] blur-3xl"
          style={{ backgroundColor: "#5B2E83" }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute top-32 -left-10 w-[320px] h-[320px] rounded-full opacity-[0.10] blur-3xl"
          style={{ backgroundColor: "#2F8F3C" }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute top-72 left-32 w-[280px] h-[280px] rounded-full opacity-[0.08] blur-3xl hidden md:block"
          style={{ backgroundColor: "#0E5FB0" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="max-w-3xl mb-12">
            {/* Top eyebrow row — branded count chip + tag */}
            <div className="flex items-center gap-2.5 flex-wrap mb-4" data-testid="services-menu-eyebrow-row">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white ring-1 ring-[#E2E8F0] shadow-[0_4px_14px_-6px_rgba(15,29,45,0.18)]"
                aria-hidden
              >
                <span className="inline-flex items-center -space-x-1">
                  <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: "#5B2E83" }} />
                  <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: "#2F8F3C" }} />
                  <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: "#0E5FB0" }} />
                </span>
                <span className="text-[11px] font-bold tracking-tight text-[#0F1D2D]">
                  {isJa ? "3 サービス" : "3 services"}
                </span>
              </span>
              <span className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold">
                {isJa ? "運営サービス" : "Operated services"}
              </span>
            </div>

            {/* Headline with an accent rail to add structure on mobile */}
            <div className="relative pl-4 md:pl-5">
              <span
                aria-hidden
                className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-gradient-to-b from-[#5B2E83] via-[#2F8F3C] to-[#0E5FB0]"
              />
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tighter leading-[1.1]",
                ].join(" ")}
              >
                {isJa
                  ? "NoJo Enterpriseが運営する3つのサービス。"
                  : "The three services operated by NoJo Enterprise."}
              </h2>
              <p className="mt-4 text-[14.5px] leading-[1.8] text-[#475569] max-w-2xl">
                {isJa
                  ? "それぞれのサービスは独立した事業として運営されています。詳細な機能、料金プラン、最新の公開アップデートは、各サービスの詳細ページにてご確認いただけます。"
                  : "Each service operates as an independent business. Detailed capabilities, plans and the most recent public updates are documented on the individual service pages."}
              </p>
            </div>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {ORDER.map((slug, i) => {
              const data = SERVICE_CONTENT[slug];
              const wordmark = WORDMARK_MAP[slug];
              const softBg = SOFT_BG_MAP[slug];
              const isETC = slug === "japanetccard" || slug === "japantollreceipts";
              const isPlanned = slug === "japanchangemoney";
              return (
                <Reveal as="li" variant="up" delay={i * 100} key={slug}>
                  <Link
                    to={`/${lang}/services/${slug}`}
                    data-testid={`services-menu-card-${slug}`}
                    style={{
                      // Per-card brand-accent ring + glow, set via inline
                      // CSS vars so the Tailwind classes below can read
                      // them. Quiet at rest (10% accent), bumps to 50% on
                      // hover, and the hover shadow tints in the same
                      // brand colour — visually continuing the
                      // purple → green → blue gradient rail from the
                      // section intro down into each card.
                      "--card-accent": data.accent,
                      "--card-accent-ring": `${data.accent}26`,    // ~15% opacity
                      "--card-accent-ring-hover": `${data.accent}80`, // ~50% opacity
                      "--card-accent-glow": `${data.accent}40`,    // ~25% opacity
                    }}
                    className="group h-full flex flex-col rounded-2xl bg-white overflow-hidden ring-1 transition-all hover:-translate-y-1 [ring-color:var(--card-accent-ring)] hover:[ring-color:var(--card-accent-ring-hover)] [box-shadow:0_0_0_rgba(0,0,0,0)] hover:[box-shadow:0_30px_55px_-25px_var(--card-accent-glow)]"
                  >
                    {/* Branded artwork header */}
                    <div
                      className="relative aspect-[5/4] w-full overflow-hidden"
                      style={{ backgroundColor: softBg }}
                    >
                      <BrandGraphic
                        variant="wave"
                        color={data.accent}
                        className="absolute inset-0 w-full h-full opacity-[0.10] pointer-events-none"
                      />
                      <span aria-hidden className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: data.accent }} />
                      {/* Per-card number chip — "01 / 02 / 03" in the
                          card's brand colour. Mirrors the "3 services"
                          count chip in the section intro, but assigns a
                          clear visual order to each card. Sits in the
                          top-right of the artwork so it doesn't fight
                          with the wordmark composition below it. */}
                      <span
                        aria-hidden
                        className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 ring-1 backdrop-blur-sm text-[10px] tracking-[0.18em] font-mono-display font-bold"
                        style={{
                          color: data.accent,
                          borderColor: `${data.accent}55`,
                        }}
                      >
                        <span
                          aria-hidden
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: data.accent }}
                        />
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="relative h-full w-full flex flex-col items-center justify-center px-6 py-6">
                        {/* Brand artwork (tollgate or planned-service icon) —
                            fixed height across all three cards so the cards
                            read as visually symmetric. */}
                        <div className="h-[120px] md:h-[130px] mb-3 flex items-center justify-center">
                          {isETC && (
                            <img
                              src={IMG.etcGateBrand}
                              alt=""
                              aria-hidden="true"
                              className="block h-full w-auto select-none"
                              draggable={false}
                              loading="lazy"
                            />
                          )}
                          {slug === "japanchangemoney" && (
                            <img
                              src={IMG.japanchangemoneyIcon}
                              alt=""
                              aria-hidden="true"
                              className="block h-full w-auto select-none"
                              draggable={false}
                              loading="lazy"
                            />
                          )}
                        </div>
                        {/* Wordmark — constrained by HEIGHT, not width, so
                            ETC (medium), Toll Receipts (long/thin) and Change
                            Money (square/bold) all sit at the same visual
                            scale. */}
                        <div className="h-[44px] md:h-[48px] w-full max-w-[260px] flex items-center justify-center">
                          <img
                            src={wordmark}
                            alt={`${data.domain} wordmark`}
                            className="block max-h-full max-w-full w-auto h-auto select-none"
                            draggable={false}
                            loading="lazy"
                          />
                        </div>
                        <div
                          className="mt-5 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold px-2.5 py-1 rounded-full"
                          style={
                            isPlanned
                              ? { color: data.accent, border: `1px solid ${data.accent}` }
                              : { color: "white", backgroundColor: "#2F8F3C" }
                          }
                        >
                          {!isPlanned && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white animate-brand-blink" />}
                          {isPlanned ? (isJa ? "準備中" : "Planned") : (isJa ? "運営中" : "Live")}
                        </div>
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="flex-1 flex flex-col p-6">
                      <h3 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[22px] md:text-[24px] font-bold tracking-tight leading-snug"].join(" ")}>
                        {data.title[lang]}
                      </h3>
                      <p
                        className="mt-2 text-[14px] font-display tracking-tight font-medium leading-snug"
                        style={{ color: data.accent }}
                      >
                        {data.headline[lang]}
                      </p>
                      <p className="mt-4 text-[13.5px] leading-[1.8] text-[#475569] flex-1">
                        {data.intro[lang].slice(0, 160)}…
                      </p>
                      <div
                        className="mt-6 inline-flex items-center justify-center gap-2 px-5 h-12 rounded-full text-white text-[13px] font-semibold tracking-tight transition-all group-hover:gap-3"
                        style={{ backgroundColor: data.accent }}
                      >
                        {isJa ? "詳細ページを見る" : "View detail page"}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ===== AI CAPABILITIES BAND ===== */}
      <section className="relative isolate bg-white border-b border-[#E2E8F0]" data-testid="services-capabilities">
        <PageSideAura testId="services-capabilities-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold mb-3">
                {isJa ? "サポートを支える技術" : "What backs the service"}
              </div>
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tighter leading-[1.1]",
                ].join(" ")}
              >
                {isJa
                  ? "お客様に最善を尽くすために、私たちは独自の技術を内製しています。"
                  : "To give every customer our best, we build our own technology in-house."}
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[14.5px] leading-[1.85] text-[#475569]">
                {isJa
                  ? "目的別に訓練したAIモデル、明確な役割分担、QAゲート、そして人による最終承認。これら一つひとつが、24時間お客様に寄り添うための「土台」として機能しています。"
                  : "Purpose-trained AI models, clear roles, QA gates, and human final approval — every piece exists to free our team up to focus on what matters: actually helping the customer."}
              </p>
            </div>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal as="li" variant="up" delay={i * 80} key={c.titleEn}>
                  <div
                    data-testid={`services-capability-${i + 1}`}
                    className="h-full rounded-2xl border border-[#E2E8F0] p-6 transition-all hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_25px_55px_-30px_rgba(15,29,45,0.35)]"
                  >
                    <span
                      aria-hidden
                      className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#5B2E83]/5 text-[#0F1D2D] mb-4"
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <h3 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-[18px] font-bold tracking-tight leading-snug"].join(" ")}>
                      {isJa ? c.titleJa : c.titleEn}
                    </h3>
                    <p className="mt-2.5 text-[13.5px] leading-[1.8] text-[#475569]">
                      {isJa ? c.bodyJa : c.bodyEn}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </ul>

          {/* AI Team mini strip — the proof point for everything above.
              ENTIRE strip is a single clickable link to /our-team. The
              "Meet the team" pill stays as a visual affordance but the
              whole purple band routes on click. */}
          <Reveal as="div" variant="up" delay={100} className="mt-10">
            <Link
              to={`/${lang}/our-team`}
              data-testid="services-ai-team-strip"
              aria-label={isJa ? "AIチームを見る" : "Meet the team"}
              className="group block rounded-2xl bg-[#5B2E83] text-white p-6 md:p-7 hover:bg-[#4A2570] hover:shadow-[0_24px_50px_-20px_rgba(91,46,131,0.55)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#5B2E83] transition-all"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
                <ul className="flex -space-x-3 shrink-0">
                  {AI_TEAM.map((m, i) => (
                    <li key={m.slug} className="relative">
                      <img
                        src={m.portrait}
                        alt={isJa ? m.nameJa : m.nameEn}
                        className="h-12 w-12 rounded-full object-cover ring-2 ring-[#0F1D2D]"
                        style={{ zIndex: AI_TEAM.length - i }}
                        loading="lazy"
                      />
                    </li>
                  ))}
                </ul>
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] tracking-[0.26em] font-mono-display uppercase font-bold text-[#FFD166] mb-1">
                    {isJa ? "私たちのAIチーム" : "Our AI team"}
                  </div>
                  <p className="text-[14px] leading-[1.75] text-white/90">
                    {isJa
                      ? "5つのAI支援ワークフローが、3つの異なるサービスに同じ運用基準を提供します。"
                      : "Five AI members, three services, one consistent operating discipline."}
                  </p>
                </div>
                <span
                  data-testid="services-ai-team-cta"
                  aria-hidden="true"
                  className="inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-4 py-2.5 rounded-full text-[12.5px] font-semibold group-hover:bg-[#F1F5F9] transition-colors shrink-0"
                >
                  {isJa ? "AIチームを見る" : "Meet the team"}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ===== AI CONSULTING (case-by-case) ===== */}
      <section className="relative isolate bg-white border-b border-[#E2E8F0]" data-testid="services-consulting">
        <PageSideAura testId="services-consulting-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div
            className="relative rounded-2xl overflow-hidden p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
            style={{ backgroundColor: "#F8F9FA" }}
          >
            <BrandGraphic
              variant="orbit"
              color="#5B2E83"
              className="absolute -right-12 -top-12 w-64 h-64 opacity-30 pointer-events-none hidden md:block"
            />
            <div className="relative md:col-span-7">
              <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.28em] font-mono-display text-[#5B2E83] uppercase font-bold">
                <Lightbulb className="h-3.5 w-3.5" strokeWidth={2.25} />
                {isJa ? "AIコンサルティング（案件単位）" : "AI Consulting — case by case"}
              </div>
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
                ].join(" ")}
              >
                {isJa
                  ? "自社のAIワークフロー設計に、当社の知見を活かしたい組織様へ。"
                  : "For organizations that want our operating playbook applied to their own AI workflows."}
              </h2>
              <p className="mt-4 text-[14px] leading-[1.85] text-[#475569] max-w-2xl">
                {isJa
                  ? "私たちは自社サービスの運営に集中しているため、コンサルティングは案件単位での対応です。AIパイプラインの設計、役割分担、QAゲート、人によるレビューの設計など、必要に応じて専門的なアドバイスをご提供します。"
                  : "We focus on running our own services, so consulting work is taken on a strictly case-by-case basis. When availability allows, we offer expertise on AI pipeline design, role separation, QA gates and human-review architecture."}
              </p>
              <p className="mt-3 text-[12px] text-[#94A3B8]">
                {isJa
                  ? "※ お問い合わせフォームから具体的な内容をお知らせいただければ、対応可否を含めてご返信いたします。"
                  : "Tell us what you're trying to build via the contact form and we'll reply with whether — and how — we can help."}
              </p>
            </div>
            <div className="relative md:col-span-5 flex md:justify-end">
              <Link
                to={`/${lang}/contact`}
                data-testid="services-consulting-cta"
                className="inline-flex items-center gap-2 bg-[#0E5FB0] text-white px-5 py-3 rounded-full text-[13px] font-semibold hover:bg-[#084785] transition-colors"
              >
                {isJa ? "お見積もり・お問い合わせ" : "Request a quote"}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST STRIP ===== */}
      <section className="bg-[#F8F9FA] border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex items-start gap-4">
          <ShieldCheck className="h-5 w-5 text-[#64748B] shrink-0 mt-0.5" strokeWidth={1.6} />
          <p className="text-[12px] leading-[1.85] text-[#475569] max-w-4xl">
            {isJa
              ? "NoJo Enterpriseの運営サービスはすべて独立して運営されています。NEXCO・ETC利用照会サービス・金融機関・政府機関の公式サービスではありません。"
              : "All NoJo Enterprise services run independently. We are not NEXCO, the ETC Usage Inquiry Service, a financial institution, or any government agency."}
          </p>
        </div>
      </section>
    </article>
  );
}
